import {
  getMessaging,
  getToken,
  onMessage,
  MessagePayload,
} from 'firebase/messaging';
import { firebaseApp } from '@/utils/firebase';
import { env } from '@/utils/env';
import { ROLES, Role } from '@/constants/roles.constants';
import {
  registerNotificationToken,
  deleteNotificationToken,
} from '@/api/notifications.api';
import { fcmInfo, fcmWarn, fcmError } from './logger';
import { PUSH_RECEIVED_EVENT } from './events';

const VAPID_KEY = env.VITE_FIREBASE_VAPID_KEY;

// Drivers get reminders; managers and executives get survey alerts.
export const PUSH_ROLES: Role[] = [
  ROLES.DRIVER,
  ROLES.MANAGER,
  ROLES.EXECUTIVE,
];

let messaging: ReturnType<typeof getMessaging> | null = null;

/**
 * Check if FCM is supported in this browser
 */
export function isFCMSupported(): boolean {
  const hasNotification = 'Notification' in window;
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const supported = hasNotification && hasServiceWorker && hasPushManager;

  if (!supported) {
    fcmWarn('fcm_not_supported', {
      hasNotification,
      hasServiceWorker,
      hasPushManager,
    });
  }

  return supported;
}

/**
 * Get Firebase Messaging instance
 */
export function getMessagingInstance() {
  if (!messaging && isFCMSupported()) {
    try {
      messaging = getMessaging(firebaseApp);
    } catch (err) {
      fcmError('get_messaging_instance_failed', {
        error: (err as Error).message,
      });
      return null;
    }
  }
  return messaging;
}

/**
 * Request notification permission and get FCM token
 * @returns FCM token or null if permission denied/error
 */
export async function requestNotificationPermission(): Promise<string | null> {
  if (!isFCMSupported()) {
    return null;
  }

  try {
    const permissionBefore = Notification.permission;

    // Skip requestPermission() if already granted — on iOS Safari re-calling it
    // can return 'denied' even when permission was previously granted (iOS
    // re-evaluates from Settings), which causes token retrieval to fail.
    let permission = permissionBefore;
    if (permissionBefore !== 'granted') {
      permission = await Notification.requestPermission();
    }

    fcmInfo('permission_result', {
      permissionBefore,
      permissionAfter: permission,
      skippedPrompt: permissionBefore === 'granted',
    });

    if (permission !== 'granted') {
      return null;
    }

    const messagingInstance = getMessagingInstance();
    if (!messagingInstance) {
      fcmError('get_token_failed', { reason: 'no_messaging_instance' });
      return null;
    }
    if (!VAPID_KEY) {
      fcmError('get_token_failed', { reason: 'no_vapid_key' });
      return null;
    }

    if ('serviceWorker' in navigator) {
      const swReg = await navigator.serviceWorker.getRegistration();
      fcmInfo('sw_state_before_get_token', {
        state: swReg?.active?.state ?? 'none',
        scope: swReg?.scope ?? 'none',
      });
    }

    const token = await getToken(messagingInstance, { vapidKey: VAPID_KEY });

    if (token) {
      fcmInfo('token_obtained', { tokenPrefix: token.slice(0, 12) });
    } else {
      fcmWarn('get_token_empty', {});
    }

    return token || null;
  } catch (err) {
    fcmError('request_permission_failed', { error: (err as Error).message });
    return null;
  }
}

/**
 * Listen for foreground messages (when app is open)
 * @param callback Function to call when message received
 */
export function onForegroundMessage(
  callback: (payload: MessagePayload) => void,
) {
  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) {
    fcmWarn('foreground_listener_skipped', { reason: 'no_messaging_instance' });
    return null;
  }

  return onMessage(messagingInstance, (payload) => {
    fcmInfo('foreground_message_received', {
      hasData: !!payload.data,
      hasNotification: !!payload.notification,
    });
    // Only refresh the page here. The service worker shows the pop-up, since
    // Safari can hand one push to a page several times.
    window.dispatchEvent(new Event(PUSH_RECEIVED_EVENT));
    callback(payload);
  });
}

/**
 * Register FCM token with backend
 * @param token FCM token
 */
export async function registerTokenWithBackend(
  token: string,
): Promise<boolean> {
  try {
    await registerNotificationToken({
      token,
      deviceInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
      },
    });
    fcmInfo('token_registered', { tokenPrefix: token.slice(0, 12) });
    return true;
  } catch (err) {
    fcmError('token_registration_failed', { error: (err as Error).message });
    return false;
  }
}

/**
 * Clean up the FCM token for this device from the backend.
 * Called when a non-driver logs in so the previous driver's token is unlinked.
 */
export async function cleanupFCMTokenForDevice(): Promise<void> {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  try {
    const messagingInstance = getMessagingInstance();
    if (!messagingInstance) return;
    const token = await getToken(messagingInstance, { vapidKey: VAPID_KEY });
    if (token) {
      await deleteNotificationToken(token);
    }
  } catch {
    // ignored
  }
}

/**
 * Initialize FCM for a logged-in driver
 * Automatically requests permission and registers token
 */
export async function initializeFCMForUser(): Promise<void> {
  if (!isFCMSupported()) return;

  try {
    fcmInfo('fcm_init_start', {
      permission: Notification.permission,
      standalone:
        'standalone' in navigator
          ? String(
              (navigator as Navigator & { standalone?: boolean }).standalone,
            )
          : 'n/a',
    });

    const token = await requestNotificationPermission();
    if (!token) {
      fcmWarn('fcm_init_no_token', { permission: Notification.permission });
      return;
    }

    const registered = await registerTokenWithBackend(token);
    if (registered) {
      onForegroundMessage(() => {
        // the service worker shows the notification
      });
      fcmInfo('fcm_init_complete', {});
    }
  } catch (err) {
    fcmError('fcm_init_failed', { error: (err as Error).message });
  }
}
